import { useQuery } from "@tanstack/react-query";
import { pathPickBasketQueryApi } from "@/features/pathpick/features/basket/api/query";
import useGetNaverPartnerStatus from "@/features/pathpick/features/basket/hooks/query/useGetNaverPartnerStatus";
import { PathPickNaverPartnerAddressBooksResponse } from "@/features/pathpick/features/basket/types/response";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import { toCamelKeyObject } from "@/utils/case";
import { isRetryableApiError } from "@/utils/axios-client-error";

// 네이버 제휴 판매자 주소록 조회 — 해외 출고지가 등록됐는지 판정하는 소스다.
//
// 주소록은 스마트스토어 API를 대신 호출해 받아오므로 판매자ID가 연동된 뒤에만 읽을 수 있다 —
// 미연동 상태에서 요청이 실패하지 않도록 연결 상태를 확인한 뒤에만 조회한다.
const useGetNaverPartnerAddressBooks = () => {
  const { companyId } = useCompanyIdentity();
  const {
    naverPartnerStatus,
    isError: isNaverPartnerStatusError,
    error: naverPartnerStatusError,
    errorUpdatedAt: naverPartnerStatusErrorUpdatedAt,
    isFetching: isNaverPartnerStatusFetching,
    refetch: refetchNaverPartnerStatus,
  } = useGetNaverPartnerStatus();

  const isNaverPartnerConnected = Boolean(naverPartnerStatus?.connected);

  const {
    data,
    isPending,
    isFetching,
    dataUpdatedAt,
    isError,
    error,
    errorUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: ["pathpick", "naver-partner-address-books", companyId],
    queryFn: async () => {
      const data = await pathPickBasketQueryApi.getNaverPartnerAddressBooks(
        companyId as number
      );
      return toCamelKeyObject(data) as PathPickNaverPartnerAddressBooksResponse;
    },
    enabled: Boolean(companyId) && isNaverPartnerConnected,
    retry: (failureCount, err) => isRetryableApiError(err) && failureCount < 1,
  });

  return {
    // enabled:false는 새 요청만 막고 캐시된 값은 그대로 돌려주므로 미연동이면 여기서 잘라낸다 —
    // "미연동 = 주소록을 알 수 없음"이 모든 소비처에서 같게 성립해야 한다.
    // 출고지/일반 두 종류로 좁혀 받으므로 한 페이지로 충분하다 — 페이지를 넘기지 않는다.
    addressBooks: isNaverPartnerConnected ? data?.addressBooks : undefined,
    // 소비처가 연결 상태를 따로 조회하지 않고 이 훅과 같은 판정을 쓰도록 함께 돌려준다.
    isNaverPartnerConnected,
    // connected:false(미연동)와 "연결 상태를 못 읽음"은 다르다 — 후자를 미연동으로 안내하면
    // 이미 연동한 사용자를 스마트스토어 관리자로 헛걸음시킨다.
    // 다시 불러오다 실패해도 앞서 받아둔 값이 있으면 그 값으로 계속 판정한다 — 화면이 뒤집히지 않는다.
    isNaverPartnerStatusUnknown:
      isNaverPartnerStatusError && naverPartnerStatus === undefined,
    isPending,
    // 어느 쪽을 다시 부르든 새로고침 버튼은 같은 자리에 있으므로 로딩도 합쳐서 본다.
    isFetching: isFetching || isNaverPartnerStatusFetching,
    dataUpdatedAt,
    isError,
    error,
    // 실패한 쿼리의 error는 캐시에 남아 컴포넌트를 다시 마운트해도 그대로 읽힌다 —
    // 에러 알림이 "지난번 실패의 잔상"과 "방금 실패"를 구분하려면 확정 시각이 필요하다.
    errorUpdatedAt,
    // 토스트가 실패 원인을 구분해 알릴 수 있도록 연결 상태 쪽 에러도 같이 넘긴다.
    statusError: naverPartnerStatusError,
    statusErrorUpdatedAt: naverPartnerStatusErrorUpdatedAt,
    // 연결 상태 조회가 실패해 주소록이 비활성인 동안에는 주소록만 다시 불러도 아무 일도 없다 —
    // 판정의 뿌리인 연결 상태부터 다시 확인한다.
    refetch: () =>
      isNaverPartnerStatusError ? refetchNaverPartnerStatus() : refetch(),
  };
};

export default useGetNaverPartnerAddressBooks;
